import { useEffect, useRef } from "react";
import { createSanctumRuntime } from "@/components/sanctumWorldRuntime";

const COMPACT_VIEWPORT_QUERY = "(max-width: 767px)";

const attachSanctumWorld = ({ mount, progressRef, onReady, reducedMotion }) => {
  if (reducedMotion) {
    mount.dataset.renderQuality = "reduced-motion";
    onReady?.(false);
    return undefined;
  }

  const compactViewport = window.matchMedia(COMPACT_VIEWPORT_QUERY);
  let runtime = null;

  const initialize = () => {
    if (runtime || compactViewport.matches) return;
    try {
      runtime = createSanctumRuntime({ mount, progressRef });
      onReady?.(!runtime.usesFallback);
    } catch (error) {
      runtime = null;
      mount.dataset.renderQuality = "css-fallback";
      onReady?.(false);
    }
  };

  const teardown = () => {
    const currentRuntime = runtime;
    runtime = null;
    currentRuntime?.dispose();
    onReady?.(false);
  };

  if (typeof window.IntersectionObserver !== "function") {
    mount.dataset.renderQuality = "css-fallback";
    return teardown;
  }

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (compactViewport.matches) return;
      if (entry.isIntersecting) initialize();
      runtime?.setVisible(entry.isIntersecting);
    },
    { rootMargin: "80% 0px", threshold: 0.01 }
  );

  const onBreakpointChange = (event) => {
    if (event.matches) {
      observer.unobserve(mount);
      teardown();
    } else {
      observer.observe(mount);
    }
  };

  const onContextLost = (event) => {
    event.preventDefault();
    teardown();
    mount.dataset.renderQuality = "css-fallback";
  };

  const refresh = () => {
    if (compactViewport.matches) return;
    const rect = mount.getBoundingClientRect();
    if (rect.bottom >= -window.innerHeight && rect.top <= window.innerHeight * 2) {
      initialize();
      runtime?.setVisible(true);
    }
  };

  if (compactViewport.addEventListener) compactViewport.addEventListener("change", onBreakpointChange);
  else compactViewport.addListener(onBreakpointChange);
  mount.addEventListener("webglcontextlost", onContextLost, true);
  window.addEventListener("pageshow", refresh);
  window.addEventListener("threshold:layout-ready", refresh);
  if (!compactViewport.matches) observer.observe(mount);

  return () => {
    observer.disconnect();
    if (compactViewport.removeEventListener) compactViewport.removeEventListener("change", onBreakpointChange);
    else compactViewport.removeListener(onBreakpointChange);
    mount.removeEventListener("webglcontextlost", onContextLost, true);
    window.removeEventListener("pageshow", refresh);
    window.removeEventListener("threshold:layout-ready", refresh);
    teardown();
  };
};

export default function SanctumWorld({ progressRef, onReady, reducedMotion }) {
  const mountRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current) return undefined;
    return attachSanctumWorld({ mount: mountRef.current, progressRef, onReady, reducedMotion });
  }, [onReady, progressRef, reducedMotion]);

  return <div ref={mountRef} className="th-webgl" data-testid="sanctum-webgl" aria-hidden="true" />;
}